// 1. React imports
import { useState } from 'react';

// 2. External libraries
import { useUser } from '@clerk/clerk-react';
import { TriangleAlert } from 'lucide-react';

// 3. Internal components
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert';
import { Button } from '~/components/ui/button';
import { LoadingSpinner } from '~/components/common/LoadingSpinner';
import { WelcomeStep } from './telegram/WelcomeStep';
import { ConnectTelegramStep } from './telegram/ConnectTelegramStep';
import { SetupClientStep } from './telegram/SetupClientStep';
import { SendTelegramMessageStep } from './telegram/SendTelegramMessageStep';
import { RetrieveMessageStep } from './telegram/RetrieveMessageStep';
import { CompletionStep } from './telegram/CompletionStep';
import { TopicCreationStep } from '~/components/onboarding/TopicCreationStep';
import { WizardProgress } from '~/components/onboarding/WizardProgress';

// 5. Custom hooks and utilities
import { useUpdateOnboardingStateMutation } from '~/hooks/useUpdateOnboardingStateMutation';
import { useCreateTopicMutation, useUpdateTopicMutation } from '~/hooks/useTopicMutations';
import { useTopicQuery } from '~/hooks/useTopicQuery';
import { useControlledFromServer } from '~/hooks/useControlledFromServer';

/**
 * Props for OnboardingWizardTelegram component.
 *
 * Per §TS.3.2: Descriptive props interface naming.
 */
export interface OnboardingWizardTelegramProps {
  onComplete: () => void;
}

/**
 * Telegram-based onboarding wizard orchestrator.
 *
 * Manages 7-step flow: Welcome → Topic Creation → Setup Client → Connect Telegram →
 * Send Message → Retrieve Message → Completion. Tracks two separate connections
 * (webhook-only and read-capable), stores API key after Step 3, and updates Clerk
 * metadata on transitions.
 *
 * Per §TS.3.1: Function declaration for named export.
 *
 * State Management:
 * - currentStep: 1-7
 * - topicId: string | null (from Step 2)
 * - topicName: string (from Step 2)
 * - mcpRestConnectionId: string | null (Step 3 read-capable connection)
 * - clientType: "mcp" | "rest" | null (from Step 3)
 * - apiKey: string | null (from Step 3, passed to Step 6)
 * - telegramConnectionId: string | null (Step 4 webhook-only connection)
 * - messageId: string | null (from Step 5)
 * - messageContent: string | null (from Step 5)
 *
 * Step Flow:
 * 1. WelcomeStep → Step 2 (manual Next)
 * 2. TopicCreationStep → Step 3 (after topic created)
 * 3. SetupClientStep → Step 4 (manual Next after MCP/REST connection)
 * 4. ConnectTelegramStep → Step 5 (auto-advance on WebSocket)
 * 5. SendTelegramMessageStep → Step 6 (auto-advance on WebSocket)
 * 6. RetrieveMessageStep → Step 7 (auto-advance on WebSocket)
 * 7. CompletionStep → Complete (manual navigation or onComplete)
 *
 * @example
 * ```tsx
 * <OnboardingWizardTelegram onComplete={() => navigate('/topics')} />
 * ```
 */
export function OnboardingWizardTelegram({ onComplete }: OnboardingWizardTelegramProps) {
  const { user } = useUser();

  // Resume wizard at saved step using onboardingProgress (per spec)
  const savedProgress = user?.publicMetadata?.onboardingProgress;
  const savedStep = savedProgress?.currentStep ?? 1;
  const savedTopicId = savedProgress?.topicId ?? null;
  const savedTelegramConnectionId = savedProgress?.telegramConnectionId ?? null;
  const savedMcpRestConnectionId = savedProgress?.mcpRestConnectionId ?? null;
  const savedClientType = savedProgress?.clientType ?? null;
  const savedMessageId = savedProgress?.messageId ?? null;
  const savedMessageContent = savedProgress?.messageContent ?? null;
  const savedApiKey = savedProgress?.apiKey ?? null;

  // Step 6 (Retrieve Message) requires the API key, but the key is never persisted
  // to onboardingProgress (the backend schema rejects it). A user who refreshes
  // between steps 3 and 6 resumes with currentStep=6 yet no apiKey, hitting the
  // "Session data missing" dead-end. Clamp the resumed step back to Step 3 (Setup
  // Client) when the key is absent. Step 3 rotates the key from the persisted
  // mcpRestConnectionId, re-establishing apiKey so the user can continue. Persisted
  // connection ids and clientType are preserved, so no progress is lost.
  const SETUP_CLIENT_STEP = 3;
  const FIRST_API_KEY_STEP = 6;
  const initialStep =
    savedStep >= FIRST_API_KEY_STEP && !savedApiKey ? SETUP_CLIENT_STEP : savedStep;

  // Per §TS.3.3: useState with explicit types for wizard state
  const [currentStep, setCurrentStep] = useState<number>(initialStep);
  const [topicId, setTopicId] = useState<string | null>(savedTopicId);
  // Connection IDs tracked via Clerk metadata; only setters needed locally
  const [mcpRestConnectionId, setMcpRestConnectionId] = useState<string | null>(
    savedMcpRestConnectionId
  );
  const [clientType, setClientType] = useState<'mcp' | 'rest' | null>(savedClientType);
  const [apiKey, setApiKey] = useState<string | null>(savedApiKey);
  const [, setTelegramConnectionId] = useState<string | null>(savedTelegramConnectionId);
  const [messageId, setMessageId] = useState<string | null>(savedMessageId);
  const [messageContent, setMessageContent] = useState<string | null>(savedMessageContent);
  const [topicCreationError, setTopicCreationError] = useState<string | null>(null);
  const [isCreatingTopic, setIsCreatingTopic] = useState<boolean>(false);
  const [showClientSuccess, setShowClientSuccess] = useState<boolean>(false);
  const [showConnectionSuccess, setShowConnectionSuccess] = useState<boolean>(false);
  const [messageJustReceived, setMessageJustReceived] = useState<boolean>(false);

  // Per §TS.5.1: Full type parameters for mutations
  const createTopicMutation = useCreateTopicMutation();
  const updateTopicMutation = useUpdateTopicMutation(topicId ?? '');
  const updateOnboardingStateMutation = useUpdateOnboardingStateMutation();

  // On resume (topicId restored from onboardingProgress, e.g. after a page
  // refresh), fetch the topic so Step 2 displays its real name instead of
  // blank and knows what name is already persisted on the server.
  const resumedTopicQuery = useTopicQuery(savedTopicId ?? undefined);
  const resumedTopicName = resumedTopicQuery.data?.name ?? '';

  // Tracks the name last persisted to the backend for `topicId`, so returning
  // to Step 2 and clicking "Create Topic" again can tell whether the user
  // actually changed the name (needs a rename) or just navigated back and
  // forth (no-op, avoids creating a duplicate topic).
  const [committedTopicName, setCommittedTopicName] = useControlledFromServer(resumedTopicName);
  const [hasEditedTopicName, setHasEditedTopicName] = useState<boolean>(false);
  const [topicName, setTopicNameRaw] = useControlledFromServer(resumedTopicName, {
    isEditing: hasEditedTopicName,
  });
  const setTopicName = (name: string) => {
    setHasEditedTopicName(true);
    setTopicNameRaw(name);
  };

  /**
   * Handle welcome step completion (Step 1 → 2).
   *
   * Event handler with handle prefix.
   */
  const handleWelcomeNext = () => {
    updateOnboardingStateMutation.mutate({ currentStep: 2 });
    setCurrentStep(2);
  };

  /**
   * Handle topic creation (Step 2 → 3).
   *
   * If the user navigated Back to Step 2 and forward again without changing the
   * name, `topicId` is already set and the name matches what's persisted on the
   * backend - just advance without creating a duplicate topic. If the name was
   * changed, rename the existing topic instead of creating a new one. Only
   * creates a brand-new topic when none exists yet.
   * Uses local loading state to prevent visual artifact during mutation.
   */
  const handleTopicCreated = async (): Promise<void> => {
    setIsCreatingTopic(true);
    setTopicCreationError(null);

    try {
      let resolvedTopicId = topicId;

      if (topicId && topicName === committedTopicName) {
        // Returning to a step whose topic already matches the backend - no-op.
        resolvedTopicId = topicId;
      } else if (topicId) {
        // Name changed since the topic was created/last renamed - rename in place.
        await updateTopicMutation.mutateAsync({ name: topicName });
        setCommittedTopicName(topicName);
      } else {
        const newTopic = await createTopicMutation.mutateAsync({
          name: topicName,
          description: null,
        });
        resolvedTopicId = newTopic.id;
        setTopicId(newTopic.id);
        setCommittedTopicName(topicName);
      }

      // Update Clerk metadata with current progress
      await updateOnboardingStateMutation.mutateAsync({
        currentStep: 3,
        topicId: resolvedTopicId ?? undefined,
      });

      setCurrentStep(3);
    } catch (err) {
      console.error('Failed to create topic:', err);
      setTopicCreationError('Failed to create topic. Please try again.');
    } finally {
      setIsCreatingTopic(false);
    }
  };

  /**
   * Handle client setup (Step 3 → 4).
   *
   * Called by SetupClientStep after MCP/REST connection created.
   * Sets mcpRestConnectionId, clientType, apiKey, sets showClientSuccess flag,
   * then advances to Step 4 (Connect Telegram).
   * Awaits Clerk mutation to ensure data persistence before advancing.
   */
  const handleClientSetup = async (connectionId: string, type: 'mcp' | 'rest', key: string) => {
    setMcpRestConnectionId(connectionId);
    setClientType(type);
    setApiKey(key);
    setShowClientSuccess(true);
    try {
      await updateOnboardingStateMutation.mutateAsync({
        currentStep: 4,
        mcpRestConnectionId: connectionId,
        clientType: type,
      });
    } catch (err) {
      console.error('Failed to persist client setup state:', err);
      // Continue anyway - local state is set
    }
    setCurrentStep(4);
  };

  /**
   * Handle Telegram connection (Step 4 → 5).
   *
   * Called by ConnectTelegramStep via WebSocket event callback.
   * Sets telegramConnectionId (webhook-only), clears showClientSuccess flag,
   * sets showConnectionSuccess flag, and advances to Step 5 (Send Message).
   */
  const handleTelegramConnected = (connectionId: string) => {
    setTelegramConnectionId(connectionId);
    setShowClientSuccess(false);
    setShowConnectionSuccess(true);
    updateOnboardingStateMutation.mutate({
      currentStep: 5,
      telegramConnectionId: connectionId,
    });
    setCurrentStep(5);
  };

  /**
   * Handle message received (Step 5 → 6).
   *
   * Called by SendTelegramMessageStep via WebSocket event callback.
   * Sets messageId, messageContent, clears showConnectionSuccess flag,
   * sets messageJustReceived flag, then advances to Step 6 (Retrieve Message).
   * Awaits Clerk mutation to ensure data persistence before advancing.
   */
  const handleMessageReceived = async (msgId: string, content: string) => {
    setMessageId(msgId);
    setMessageContent(content);
    setShowConnectionSuccess(false);
    setMessageJustReceived(true);
    try {
      await updateOnboardingStateMutation.mutateAsync({
        currentStep: 6,
        messageSent: true,
      });
    } catch (err) {
      console.error('Failed to persist message state:', err);
      // Continue anyway - local state is set
    }
    setCurrentStep(6);
  };

  /**
   * Handle retrieval completion (Step 6 → 7).
   *
   * Called by RetrieveMessageStep after message retrieval confirmed.
   * Advances to Step 7 (CompletionStep) and marks onboarding as complete.
   * Reaching Step 7 means user has completed all tutorial steps.
   * Reloads user data to sync publicMetadata before allowing navigation.
   */
  const handleRetrievalComplete = async () => {
    try {
      await updateOnboardingStateMutation.mutateAsync({
        currentStep: 7,
        messageRetrieved: true,
        completed: true,
      });
      // Reload user to sync publicMetadata before allowing navigation
      await user?.reload();
    } catch (err) {
      console.error('Failed to persist retrieval state:', err);
      // Continue anyway - local state is set
    }
    setCurrentStep(7);
  };

  /**
   * Handle final completion (Step 7 → Complete).
   *
   * Called by CompletionStep when user clicks "Go to Dashboard".
   * Onboarding is already marked complete in handleRetrievalComplete.
   * Just navigate to dashboard.
   */
  const handleFinalComplete = () => {
    // Onboarding is already marked complete in handleRetrievalComplete
    // Just navigate to dashboard
    onComplete();
  };

  /**
   * Handle skip action from any step.
   *
   * Sets onboardingSkipped: true in Clerk metadata and exits wizard.
   * Awaits Clerk mutation to ensure skip state is set before navigation.
   * Reloads Clerk user data after mutation to sync publicMetadata.
   */
  const handleSkip = async () => {
    try {
      await updateOnboardingStateMutation.mutateAsync({ skipped: true });
      // Reload user to sync publicMetadata before navigation
      await user?.reload();
    } catch (err) {
      console.error('Failed to mark onboarding skipped:', err);
      // Continue anyway - user should be able to exit
    }
    onComplete();
  };

  /**
   * Handle back navigation between steps.
   *
   * Decrements currentStep while preserving wizard state for forward navigation.
   * Clears success notification flags when navigating back to avoid showing
   * stale success messages on re-entry.
   */
  const handleBack = (): void => {
    if (currentStep > 1) {
      // Clear success flags when navigating back
      if (currentStep === 4) {
        setShowClientSuccess(false);
      }
      if (currentStep === 5) {
        setShowConnectionSuccess(false);
      }
      if (currentStep === 6) {
        setMessageJustReceived(false);
      }
      setCurrentStep(currentStep - 1);
    }
  };

  // On resume past Step 1, the topic name is fetched asynchronously (see
  // resumedTopicQuery above). Block rendering the step body until it resolves
  // so step copy never briefly reads "to ." before the name loads.
  const isResumingTopicName = !!savedTopicId && resumedTopicQuery.isLoading;

  if (isResumingTopicName) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-8">
        <LoadingSpinner message="Loading your progress..." />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <div className="min-h-[600px]">
        {/* Step Progress Indicator */}
        {currentStep < 7 && (
          <div className="mb-8">
            <WizardProgress currentStep={currentStep} totalSteps={6} />
          </div>
        )}

        {/* Step 1: Welcome */}
        {currentStep === 1 && (
          <WelcomeStep onNext={handleWelcomeNext} onSkip={() => void handleSkip()} />
        )}

        {/* Step 2: Topic Creation */}
        {currentStep === 2 && (
          <TopicCreationStep
            selectedUseCase={null}
            topicName={topicName}
            onTopicNameChange={setTopicName}
            onCreateTopic={handleTopicCreated}
            isCreating={isCreatingTopic}
            error={topicCreationError}
            onBack={handleBack}
            onSkip={() => void handleSkip()}
          />
        )}

        {/* Step 3: Setup Client */}
        {currentStep === 3 && topicId && (
          <SetupClientStep
            topicId={topicId}
            topicName={topicName}
            onConnectionCreated={(connectionId, type, key) =>
              void handleClientSetup(connectionId, type, key)
            }
            onBack={handleBack}
            onSkip={() => void handleSkip()}
            existingConnectionId={mcpRestConnectionId}
            existingClientType={clientType}
            existingApiKey={apiKey}
          />
        )}

        {/* Step 4: Connect Telegram */}
        {currentStep === 4 && topicId && (
          <ConnectTelegramStep
            topicId={topicId}
            topicName={topicName}
            onConnectionCreated={handleTelegramConnected}
            onBack={handleBack}
            onSkip={() => void handleSkip()}
            showClientSuccess={showClientSuccess}
          />
        )}

        {/* Step 5: Send Message */}
        {currentStep === 5 && topicId && (
          <SendTelegramMessageStep
            topicId={topicId}
            onMessageReceived={(msgId, content) => void handleMessageReceived(msgId, content)}
            onBack={handleBack}
            onSkip={() => void handleSkip()}
            showConnectionSuccess={showConnectionSuccess}
          />
        )}

        {/* Step 6: Retrieve Message - only requires topicId, clientType, apiKey */}
        {/* messageId/messageContent are optional - preview card hidden if unavailable */}
        {currentStep === 6 && topicId && clientType && apiKey && (
          <RetrieveMessageStep
            topicId={topicId}
            messageId={messageId}
            messageContent={messageContent}
            clientType={clientType}
            apiKey={apiKey}
            onComplete={() => void handleRetrievalComplete()}
            onBack={handleBack}
            onSkip={() => void handleSkip()}
            showMessageSuccess={messageJustReceived}
          />
        )}

        {/* Step 6: Fallback when essential data is missing */}
        {currentStep === 6 && (!topicId || !clientType || !apiKey) && (
          <div>
            <Alert variant="warning" className="mb-6">
              <TriangleAlert />
              <AlertTitle>Session data missing</AlertTitle>
              <AlertDescription>
                Some required data from previous steps is missing. Please go back and complete the
                previous steps again.
              </AlertDescription>
            </Alert>

            <div className="flex justify-end gap-4">
              <Button variant="ghost" onClick={() => void handleSkip()}>
                Skip onboarding
              </Button>
              <Button variant="outline" onClick={handleBack}>
                Go Back
              </Button>
            </div>
          </div>
        )}

        {/* Step 7: Completion */}
        {currentStep === 7 && topicId && (
          <CompletionStep topicId={topicId} onComplete={handleFinalComplete} />
        )}

        {/* Step 7: Fallback when topicId is missing */}
        {currentStep === 7 && !topicId && (
          <div>
            <Alert variant="warning" className="mb-6">
              <TriangleAlert />
              <AlertTitle>Session data missing</AlertTitle>
              <AlertDescription>
                Topic ID is missing. Please restart the onboarding process.
              </AlertDescription>
            </Alert>

            <div className="flex justify-end gap-4">
              <Button variant="ghost" onClick={() => void handleSkip()}>
                Skip onboarding
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
